// Client-only auth guard. Renders children when the visitor's role matches;
// otherwise redirects to /login (preserving where they were headed).
// Demo IDs stay viewable so the marketing preview is never blocked.
import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Shield } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import type { AppRole } from "@/lib/db/models";

const DEMO_IDS = new Set(["demo", "INV-2026-0704-0142"]);

export function isDemoId(id?: string) {
  return !!id && DEMO_IDS.has(id);
}

interface AuthGateProps {
  children: ReactNode;
  /** Which roles may view. Defaults to "any signed-in user". */
  allow?: AppRole[];
  /** If true and the current path contains a demo id, bypass the gate. */
  allowDemo?: boolean;
}

export function AuthGate({ children, allow, allowDemo }: AuthGateProps) {
  const { session, roles, loading } = useAuth();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });

  const isDemoPath = allowDemo && [...DEMO_IDS].some((id) => path.endsWith("/" + id));

  const authorized =
    !!session && (!allow || allow.length === 0 || allow.some((r) => roles.includes(r)));

  // Capture the originally-requested route ONCE so subsequent renders — which
  // may already reflect the in-flight navigation to /login — cannot overwrite
  // the redirect target and yield ?redirect=/login.
  const capturedPath = useRef<string | null>(null);
  if (
    capturedPath.current === null &&
    path &&
    !path.startsWith("/login") &&
    !path.startsWith("/register")
  ) {
    capturedPath.current = path;
  }
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (loading || isDemoPath) return;
    if (!session) {
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      const target =
        capturedPath.current && !capturedPath.current.startsWith("/login")
          ? capturedPath.current
          : "/dashboard";
      // Preserve query string (e.g. ?id=...) so redirect returns the user to
      // the exact same view.
      let qs = "";
      if (search && typeof search === "object" && Object.keys(search).length > 0) {
        try {
          const params: Record<string, string> = {};
          for (const [k, v] of Object.entries(search)) {
            if (v == null) continue;
            params[k] = String(v);
          }
          const s = new URLSearchParams(params).toString();
          if (s) qs = "?" + s;
        } catch {
          /* ignore */
        }
      }
      navigate({ to: "/login", search: { redirect: target + qs } });
      return;
    }
    if (allow && allow.length > 0 && !allow.some((r) => roles.includes(r))) {
      // Signed in but wrong role — send to their own workspace.
      const dest =
        roles.includes("staff") || roles.includes("admin") ? "/operations" : "/dashboard";
      navigate({ to: dest });
    }
  }, [loading, session, roles, allow, isDemoPath, navigate, search]);

  if (isDemoPath || authorized) return <>{children}</>;

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Shield className="h-4 w-4 animate-pulse text-navy" />
        Checking your session…
      </div>
    </div>
  );
}
